import { IconGitHub } from "@/assets/icons/geist/IconGitHub";
import { IconGoogle } from "@/assets/icons/geist/IconGoogle";
import { IconKey2 } from "central-icons/IconKey2";
import { Button } from "@/components/flexkit/Button";
import { Card } from "@/components/flexkit/Card";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Text } from "@/components/podkit/typography/Text";
import { Tooltip } from "@/components/Tooltip";
import { TrackLocations } from "@/hooks/use-segment";
import { useUpdateSSOConfiguration, type PlainDomainVerification } from "@/queries/organization-queries";
import { formatError } from "@/utils/errors";
import { Switch } from "@/components/Switch";
import {
    DomainVerificationState,
    SSOConfigurationState,
    UpdateSSOConfigurationRequest,
    type SSOConfiguration,
} from "gitpod-next-api/gitpod/v1/organization_pb";
import { useCallback, useMemo, useState, type FC } from "react";
import { DomainPill } from "@/routes/settings/login/DomainPill";
import { DomainSelector } from "@/routes/settings/login/DomainSelector";

type SSOConfigCardV2Props = {
    config: SSOConfiguration;
    deactivateable: boolean;
    editable?: boolean;
    onEdit?: (config: SSOConfiguration) => void;
    /** All domain verifications for the Add dropdown */
    domainVerifications?: PlainDomainVerification[];
    /** Called when user wants to create a new domain, receives the config ID to add the domain to */
    onNewDomain?: (configId: string) => void;
};

export const CustomSSOConfigCardV2: FC<SSOConfigCardV2Props> = ({
    config,
    deactivateable,
    editable = true,
    onEdit,
    domainVerifications = [],
    onNewDomain,
}) => {
    const { toast } = useToast();
    const updateSSOConfig = useUpdateSSOConfiguration();

    // Get all email domains from the config (use emailDomains array, fallback to single emailDomain)
    const emailDomains = useMemo(() => {
        if (config.emailDomains && config.emailDomains.length > 0) {
            return config.emailDomains;
        }
        return config.emailDomain ? [config.emailDomain] : [];
    }, [config.emailDomains, config.emailDomain]);

    // Check if a domain is verified
    const isDomainVerified = useCallback(
        (domain: string) => {
            const verification = domainVerifications.find((v) => v.domain === domain);
            return verification?.state === DomainVerificationState.VERIFIED;
        },
        [domainVerifications],
    );

    // Use issuer URL as title
    const title = config.displayName || config.issuerUrl || "Custom SSO";

    // Handle removing a domain from the SSO config
    const handleRemoveDomain = useCallback(
        async (domainToRemove: string) => {
            const newDomains = emailDomains.filter((d) => d !== domainToRemove);
            if (newDomains.length === 0) {
                toast({
                    title: "Cannot remove the last domain",
                    description: "An SSO configuration must have at least one domain.",
                });
                return;
            }

            try {
                await updateSSOConfig.mutateAsync(
                    new UpdateSSOConfigurationRequest({
                        ssoConfigurationId: config.id,
                        emailDomains: newDomains,
                    }),
                );
                toast({
                    title: `Removed ${domainToRemove} from SSO configuration`,
                });
            } catch (error) {
                toast({
                    title: "Failed to remove domain",
                    description: formatError(error),
                });
            }
        },
        [config.id, emailDomains, toast, updateSSOConfig],
    );

    // Handle adding domains to the SSO config
    const handleAddDomains = useCallback(
        async (domainsToAdd: string[]) => {
            const newDomains = [...emailDomains, ...domainsToAdd];

            try {
                await updateSSOConfig.mutateAsync(
                    new UpdateSSOConfigurationRequest({
                        ssoConfigurationId: config.id,
                        emailDomains: newDomains,
                    }),
                );
                toast({
                    title: `Added ${domainsToAdd.join(", ")} to SSO configuration`,
                });
            } catch (error) {
                toast({
                    title: "Failed to add domains",
                    description: formatError(error),
                });
            }
        },
        [config.id, emailDomains, toast, updateSSOConfig],
    );

    const toggle = (
        <EnablementToggle config={config} title={title} deactivateable={deactivateable} editable={editable} />
    );

    const actions = (
        <>
            <Button
                disabled={!editable}
                variant={"secondary"}
                size={"sm"}
                onClick={() => onEdit?.(config)}
                data-tracking-id="edit-sso-custom-sso-config-card"
            >
                Edit
            </Button>
            {onNewDomain && (
                <DomainSelector
                    domainVerifications={domainVerifications}
                    assignedDomains={emailDomains}
                    onAddDomains={handleAddDomains}
                    onRemoveDomain={handleRemoveDomain}
                    onNewDomain={() => onNewDomain(config.id)}
                    disabled={!editable}
                />
            )}
        </>
    );

    return (
        <Card className="flex w-full flex-col gap-3 bg-surface-glass p-4" variant="bordered">
            <div className="flex flex-wrap items-center gap-3">
                {toggle}
                <Text className="text-base font-medium text-content-primary">{title}</Text>
                <div className="ml-auto flex items-center gap-2">{actions}</div>
            </div>
            {/* Domain pills */}
            {emailDomains.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {emailDomains.map((domain) => (
                        <DomainPill
                            key={domain}
                            domain={domain}
                            verified={isDomainVerified(domain)}
                            onRemove={emailDomains.length > 1 ? () => handleRemoveDomain(domain) : undefined}
                            disabled={!editable || updateSSOConfig.isPending}
                        />
                    ))}
                </div>
            )}
        </Card>
    );
};

export const BuiltinSSOConfigCardV2: FC<{ config: SSOConfiguration; deactivateable: boolean; editable?: boolean }> = ({
    config,
    deactivateable,
    editable = true,
}) => {
    const title = config ? getTitle(config) : "";

    const icon = config ? getIcon(config) : undefined;
    const toggle = config && (
        <EnablementToggle
            title={title || getTitle(config)}
            config={config}
            deactivateable={deactivateable}
            editable={editable}
        />
    );

    return (
        <Card className="flex w-[550px] flex-col gap-4 bg-surface-glass p-4" variant="bordered">
            <div className="flex items-center gap-2">
                {toggle}
                {icon ?? <IconKey2 size={24} className="text-content-primary" />}
                <p className="text-base font-normal text-content-primary">{title}</p>
            </div>
        </Card>
    );
};

const getTitle = (config: SSOConfiguration) => {
    if (config.issuerUrl?.includes("github")) {
        return "GitHub";
    }
    if (config.issuerUrl?.includes("google")) {
        return "Google";
    }
    return "Unknown";
};

const getIcon = (config: SSOConfiguration) => {
    if (config.issuerUrl?.includes("github")) {
        return <IconGitHub size="lg" className="size-6" />;
    }
    if (config.issuerUrl?.includes("google")) {
        return <IconGoogle size="lg" className="size-6" />;
    }
    return undefined;
};

const EnablementToggle: FC<{
    config: SSOConfiguration;
    title: string;
    deactivateable: boolean;
    editable: boolean;
}> = ({ config: { id, state }, title, deactivateable, editable }) => {
    const updateSSOConfig = useUpdateSSOConfiguration();
    const { toast } = useToast();

    const [showModal, setShowModal] = useState(false);

    const isActive = state === SSOConfigurationState.SSO_CONFIGURATION_STATE_ACTIVE;

    const updateState = useCallback(
        async (state: SSOConfigurationState) => {
            try {
                await updateSSOConfig.mutateAsync(
                    new UpdateSSOConfigurationRequest({
                        ssoConfigurationId: id,
                        state,
                    }),
                );
            } catch (error) {
                toast({
                    title: "Failed to update SSO configuration",
                    description: formatError(error),
                });
            }
        },
        [id, toast, updateSSOConfig],
    );

    const handleCheckedChange = useCallback(async () => {
        if (!isActive) {
            await updateState(SSOConfigurationState.SSO_CONFIGURATION_STATE_ACTIVE);
        } else {
            setShowModal(true);
        }
    }, [isActive, updateState]);

    const handleSubmitDeactivation = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            await updateState(SSOConfigurationState.SSO_CONFIGURATION_STATE_INACTIVE);
            setShowModal(false);
        },
        [updateState],
    );

    return (
        <>
            <Tooltip content={!deactivateable && isActive ? "You can't deactivate the single remaining provider." : ""}>
                <div className="flex items-center">
                    <Switch
                        state={isActive ? "checked" : "unchecked"}
                        disabled={(!deactivateable && isActive) || !editable}
                        onToggle={handleCheckedChange}
                        id={"toggle-" + id}
                    />
                </div>
            </Tooltip>
            {showModal && (
                <Dialog open onOpenChange={setShowModal}>
                    <DialogContent
                        className="max-w-[600px]"
                        data-testid="deactivate-form-modal-content"
                        data-track-location={TrackLocations.DeactivateSSOModal}
                    >
                        <DialogHeader>
                            <DialogTitle>Deactivating {title} log in </DialogTitle>
                            <DialogDescription />
                        </DialogHeader>

                        <DialogBody className="overflow-x max-w-full">
                            <form
                                id="deactivate-form"
                                onSubmit={handleSubmitDeactivation}
                                className="flex flex-col gap-4"
                            >
                                <Text className="text-base">Users will no longer be able to log in with {title}.</Text>
                                <Text className="text-base">
                                    They will need to create a new account using one of the other available log in
                                    methods.
                                </Text>
                            </form>
                        </DialogBody>

                        <DialogFooter>
                            <DialogClose asChild>
                                <Button type="button" variant="secondary" autoFocus={true}>
                                    Cancel
                                </Button>
                            </DialogClose>
                            <Button
                                type="submit"
                                variant="destructive"
                                form="deactivate-form"
                                loading={updateSSOConfig.isPending}
                                disabled={updateSSOConfig.isPending}
                                data-testid="deactivate-submit-button"
                                data-tracking-id="deactivate-sso-config-toggle"
                            >
                                Deactivate
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
};
